import { z } from 'zod';
import type { SiteEntity } from '../../types/site';
import type { AuthoredFieldDef } from '../../types/authoredEvidence';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { computeDocumentSourceIdentity } from '../sed/documentSourceIdentity';

const id = z.string().min(1).max(256).regex(/^[^/\\\u0000-\u001f\u007f]+$/).refine(value => value !== '.' && value !== '..');
const sha = z.string().regex(/^[a-f0-9]{64}$/);
export const authoredClaimSaveRequestSchema = z.object({
  ownerUid: id, siteId: id, operationId: z.string().uuid(), requirementId: id,
  expectedBasisHash: sha, schemaHash: sha,
  form: z.object({ fields: z.record(z.string(), z.union([z.string().max(20000), z.number().finite(), z.boolean()])),
    notes: z.string().max(20000), confidence: z.enum(['low', 'medium', 'high']) }).strict(),
}).strict();
export type AuthoredClaimSaveRequest = z.infer<typeof authoredClaimSaveRequestSchema>;
export const authoredClaimSaveAckSchema = z.object({
  saved: z.literal(true), ownerUid: id, siteId: id, operationId: z.string().uuid(), claimId: id,
  claimCount: z.number().int().nonnegative(), contextHash: sha, expectedBasisHash: sha, afterBasisHash: sha,
  writtenAt: z.string().datetime(), author: z.object({ uid: id, email: z.string().optional() }).strict(),
  sedSyncStatus: z.literal('pending-rebuild'),
}).strict();
export type AuthoredClaimSaveAck = z.infer<typeof authoredClaimSaveAckSchema>;

/** One source/CAS basis on both sides; telemetry and transient owner hints are excluded. */
export function authoredClaimBasisHash(site: SiteEntity): string {
  return hashCanonicalJson({ version: 'authored-claim-source-v1', siteId: site.id,
    documentSource: computeDocumentSourceIdentity(site),
    sedId: site.sedId ?? null, sedVersion: site.sedVersion ?? null,
    sedStoragePath: site.sedStoragePath ?? null, sedContentHash: site.sedContentHash ?? null,
    sedStatus: site.sedStatus ?? null, studyAreaScope: site.studyAreaScope ?? null,
    intent: site.projectIntent ?? null, location: site.location,
    boundaries: site.siteBoundaries ?? site.siteBoundary ?? null,
    studyArea: site.studyArea ?? null,
    dirtySections: site.siteEvidenceDirtySections ?? [],
  });
}
/** Client display schemas must match the maintained canonical fields/units. */
export function authoredClaimSchemaHash(fields: readonly AuthoredFieldDef[]): string {
  return hashCanonicalJson(fields.map(field => ({ key: field.key, label: field.label, type: field.type,
    unit: field.unit ?? null, enumValues: field.enumValues ?? null, readOnly: field.readOnly === true })));
}
